import clsx from "clsx";
import partition from "lodash-es/partition";
import { type FC } from "react";

import { AddTermModal } from "./AddTermModal";

import { TERM_ID_QUERY_PARAM } from "components/constants";
import { type CourseViewOption, CourseViewOptionMenu } from "components/menus/CourseViewOptionMenu";
import { TermType } from "components/server-types";
import { t } from "i18n/i18n";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { ListItem } from "mds/components/ListItem";
import { showConfirmationModal } from "mds/components/Modal";
import { TextAddButton } from "mds/components/TextAddButton";
import { storeApi } from "store/index";
import { useDnDItemOrdering, useLocalCopy } from "utils/collections";
import { useQueryParamsManager } from "utils/routing";
import { withQueryParams } from "utils/urls";

const TERMS_EVENT_PREFIX = "Home - Terms / ";

type TermsNavListProps = {
  terms: TermType[];
  canEdit: boolean;
  onOptionChange: (option: CourseViewOption) => void;
};

export const TermsNavList: FC<TermsNavListProps> = ({ terms, canEdit, onOptionChange }) => {
  const [localTerms, setLocalTerms] = useLocalCopy(terms);
  const { setQueryParams, getQueryParam } = useQueryParamsManager();
  const [archivedTerms, activeTerms] = partition(terms, (term) => term.archived_at);
  const currentTermId = getQueryParam(TERM_ID_QUERY_PARAM);

  const termCollections = [
    { label: t("home.terms.active_terms"), terms: activeTerms },
    { label: t("home.terms.archived"), terms: archivedTerms },
  ];

  const moveItemsLocally = useDnDItemOrdering(localTerms, setLocalTerms);

  const onDropItem = (droppedItem: TermType) => {
    storeApi.terms.partial_update({ id: droppedItem.id, order: droppedItem.order });
  };

  const handleCreateTerm = () => {
    showConfirmationModal({
      title: t("home.terms.add_term_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddTermModal
          eventName={`${TERMS_EVENT_PREFIX} Add Term`}
          localTerms={localTerms}
          onClose={onClose}
        />
      ),
    });
  };

  return (
    <div className="w-[240px] rounded-2xl border border-black-tint-90 p-4" role="list">
      <div className="mb-4">
        <CourseViewOptionMenu
          eventPrefix={TERMS_EVENT_PREFIX}
          selectedOption="term"
          onOptionChange={onOptionChange}
        />
      </div>

      {termCollections.map(
        ({ label, terms: collectionTerms }) =>
          collectionTerms.length > 0 && (
            <div className="mb-4" key={label}>
              <h2 className="body-xs p-2 font-medium text-black-tint-40">{label}</h2>

              {collectionTerms.map((term, index) => {
                // NOTE: DragAndDrop functionality is disabled for Terms at the moment,
                // but we want to restore it at some point in the future. See
                // https://app.asana.com/1/914015496185/project/1206079475640061/task/1210744969386349.
                return (
                  <DragAndDrop
                    // draggable={canEdit}
                    dragItemKind="term"
                    dropContainerId="term"
                    dropIndex={index}
                    // droppable={canEdit}
                    item={term}
                    key={term.id}
                    onDraggedItemHover={moveItemsLocally}
                    onDropItem={onDropItem}
                  >
                    <ListItem
                      className="my-1 min-w-12 justify-between"
                      eventName={`${TERMS_EVENT_PREFIX}term selected`}
                      eventProperties={{ term: term.title }}
                      isSelected={currentTermId === term.id}
                      label={term.title}
                      to={withQueryParams("", { [TERM_ID_QUERY_PARAM]: term.id })}
                      onClick={() => {
                        setQueryParams({ [TERM_ID_QUERY_PARAM]: term.id });
                      }}
                    >
                      <div className="flex w-full min-w-12 items-center justify-between">
                        <h3 className="body-s my-1 w-full min-w-12 cursor-pointer truncate font-medium">
                          {term.title}
                        </h3>
                      </div>
                    </ListItem>
                  </DragAndDrop>
                );
              })}
            </div>
          ),
      )}

      {canEdit && (
        <TextAddButton
          className={clsx("w-[206px] max-w-[206px]", localTerms.length && "mt-2")}
          eventName={`${TERMS_EVENT_PREFIX}${t("common.add.term")}`}
          kind="gray"
          size="s"
          title={t("common.add.term")}
          onClick={() => handleCreateTerm()}
        >
          <span className="font-medium text-black-tint-20">{t("common.add.term")}</span>
        </TextAddButton>
      )}
    </div>
  );
};
